import { Dispatch, MutableRefObject, SetStateAction } from "react";
import * as CONSTANT from "../constants";
import { CoreModifierNutritionEntry } from "../containers/nutrition/nutritionItemDetailApi";
import { FULL_SIZE, HALF_SIZE } from "../constants";

export interface Item {
  id: number;
  name: string;
  description: string;
  category: string;
  image: string;
  is_try_2_combo?: boolean;
  is_kids_combo?: boolean;
}

/** API pivot row for modifier ↔ location (note `modifier_Id` casing from backend) */
export interface IModifierLocationPivot {
  modifier_Id?: number;
  item_id?: number;
  location_id: number;
}

export interface IModifierLocationRow {
  status: string | null;
  modifier_id?: number;
  item_id?: number;
  location_id: number;
  reason: string | null;
  pivot?: IModifierLocationPivot;
}

/** Modifier row as returned on items (`is_selected`) and inside modifier groups */
export interface IItemSelectedModifier {
  id: number;
  name: string;
  calories: number | null;
  allergen: boolean;
  price: number;
  image: string;
  allergen_description: string | null;
  brink_modifier_id: number;
  brink_modifier_group_id: number;
  min: number;
  max: number;
  new_brink_modifier_id: number | null;
  kids_combo: number;
  created_at?: string;
  is_delivery: number;
  is_no_modifier: number;
  ezcater_modifier_name: string | null;
  is_selected: number;
  order: number;
  max_limit_reached?: boolean;
  modifier_group_id: number;
  modifier_group_min: number;
  modifier_group_max: number;
  modifier_group_extendable_limit: number;
  will_substitute?: boolean;
  is_nested_kids_combo?: boolean;
  is_edit_nested_kids_combo?: boolean;
  modifier_location: IModifierLocationRow[];
}

export type ItemPriceCalcType = "SELECT_ONE" | "UP_TO_MAX" | "CORE" | string;

export interface IItemTraySizeCountRow {
  item_id: number;
  modifier_group_id: number;
  item_count: number;
}

/** Nested item used as a row in `items_as_modifier` (catering / tray flows) */
export interface IItemAsModifierNested {
  id: number;
  name: string;
  sub_title: string | null;
  description: string;
  calories: number | null;
  calories_label: string | null;
  full_calories: number | null;
  full_calories_label: string | null;
  price: number;
  make_time_minutes: number | null;
  image: string;
  created_at: string;
  updated_at?: string | null;
  deleted_at: string | null;
  category_id: number;
  protein: string | null;
  is_active: number;
  is_86_able?: number;
  reward_catalog: number;
  full_loyalty_points: number | null;
  half_loyalty_points: number | null;
  full_brink_item_id: number | null;
  half_brink_item_id: number | null;
  half_brink_item_price: number | null;
  half_brink_item_group_id: number | null;
  full_brink_item_group_id: number | null;
  new_half_brink_item_id?: number | null;
  new_full_brink_item_id?: number | null;
  is_delivery: number;
  is_pickup: number;
  is_third_party: number;
  show_in_app: number;
  infused_drink?: number;
  canned_drink?: number;
  app_image?: string | null;
  half_item_label: string | null;
  half_item_short_description: string | null;
  half_item_count: number | null;
  full_item_label: string | null;
  full_item_short_description: string | null;
  full_item_count: number | null;
  counter_increment: number | null;
  is_box_lunch: number;
  is_soup_tray: number;
  is_sandwich_tray: number;
  is_salad_tray: number;
  is_package: number;
  is_full_size: number;
  is_single_item: number;
  is_create_your_own: number;
  ezcater_half_item_name: string | null;
  ezcater_full_item_name: string | null;
  image_version: string | null;
  order: number;
  is_selected: number;
  in_item: number;
  modifier_group_id: number;
  modifier_group_min: number;
  modifier_group_max: number;
  modifier_group_extendable_limit: number;
  category_name: string;
  category_full_loyalty_points: number | null;
  category_half_loyalty_points: number | null;
  category_half_loyalty_label: string | null;
  category_full_loyalty_label: string | null;
  is_selected_with_count?: boolean;
  size_one_count?: number;
  size_two_count?: number;
  modifier_location: IModifierLocationRow[];
  categories?: { id: number; name: string }[];
}

export interface Items_Modifier_Groups {
  id: number;
  name: string;
  brink_modifier_group_id?: number;
  is_customizable_menu: number;
  description?: string | null;
  label?: string;
  min?: number;
  max?: number;
  check_extendable_limit?: number;
  is_sub_item_required_modifier_group?: number;
  complimentary_modifier_group?: number;
  extendable_limit?: number;
  no_modifier_label?: string | null;
  ignore_parent_item_count?: number;
  order?: number;
  core_quantity?: number;
  core_price?: number;
  has_core?: number;
  in_core_section?: boolean;
  base?: number;
  min_mod_price?: number;
  price_calc_type?: ItemPriceCalcType;
  modifiers_groups_modifiers: {
    id: number;
    brink_modifier_id: number;
    name: string;
    price: number;
    image: string;
    calories: number | null;
    max: number;
    nutrition_facts?: CoreModifierNutritionEntry[];
    allergen?: boolean;
    allergen_description?: string | null;
    brink_modifier_group_id?: number;
    min?: number;
    new_brink_modifier_id?: number | null;
    kids_combo?: number;
    created_at?: string;
    is_delivery?: number;
    is_no_modifier?: number;
    ezcater_modifier_name?: string | null;
    is_selected?: number;
    order?: number;
    max_limit_reached?: boolean;
    modifier_group_id?: number;
    modifier_group_min?: number;
    modifier_group_max?: number;
    modifier_group_extendable_limit?: number;
    will_substitute?: boolean;
    is_nested_kids_combo?: boolean;
    is_edit_nested_kids_combo?: boolean;
    modifier_location: IModifierLocationRow[];
  }[];
}

export interface ComplementaryItems {
  id: number;
  name: string;
  description: string;
  label?: string;
  min: number;
  max: number;
  is_customizable_menu: number;
  complimentary_modifier_group: number;
  modifiers_groups_modifiers: {
    id: number;
    brink_modifier_id: number | null;
    brink_modifier_group_id: number;
    name: string;
    price: number;
    image: string;
    calories: number;
    max: number;
    is_selected: number;
    allergen_description: string | null;
    modifier_location: IModifierLocationRow[];
  }[];
}

export interface IRequiredModifiersGroupsModifiers {
  id: number;
  brink_modifier_id: number;
  name: string;
  price: number;
  image: string;
  calories: number | null;
  allergen?: boolean;
  allergen_description: string | null;
  brink_modifier_group_id: number;
  max: number;
  min?: number;
  /** API may send 0/1; treat as boolean at call sites */
  in_item: boolean;
  is_selected?: number;
  new_brink_modifier_id?: number | null;
  kids_combo?: number;
  created_at?: string;
  is_delivery?: number;
  is_no_modifier?: number;
  ezcater_modifier_name?: string | null;
  order?: number;
  max_limit_reached?: boolean;
  modifier_group_id?: number;
  modifier_group_min?: number;
  modifier_group_max?: number;
  modifier_group_extendable_limit?: number;
  modifier_count?: number;
  related_modifier_group?: number;
  count?: number;
  will_substitute?: boolean;
  is_nested_kids_combo?: boolean;
  is_edit_nested_kids_combo?: boolean;
  required_modifier_location?: IModifierLocationRow[];
  modifier_location: IModifierLocationRow[];
  quantity?: number;
  modifier_id?: number;
}

export interface IItemsRequiredModifierGroups {
  id: number;
  name: string;
  brink_modifier_group_id?: number;
  is_customizable_menu: number;
  description?: string | null;
  label?: string;
  min?: number;
  max?: number;
  check_extendable_limit?: number;
  is_sub_item_required_modifier_group?: number;
  complimentary_modifier_group?: number;
  extendable_limit?: number;
  no_modifier_label?: string | null;
  ignore_parent_item_count?: number;
  order?: number;
  base: number;
  min_mod_price?: number;
  price_calc_type?: ItemPriceCalcType;
  item_counter_increment?: number;
  half_item_count?: number;
  full_item_count?: number;
  size_one_base?: number;
  size_one_min_mod_price?: number;
  size_two_base?: number;
  size_two_min_mod_price?: number;
  required_modifiers_groups_modifiers: IRequiredModifiersGroupsModifiers[];
  items_as_modifier?: IItemAsModifierNested[];
  items_size_one?: IItemTraySizeCountRow[];
  items_size_two?: IItemTraySizeCountRow[];
}

export interface ICoreModifiers {
  id: number;
  nutrition_facts: CoreModifierNutritionEntry[];
  brink_modifier_id: number;
  brink_modifier_group_id: number;
  quantity: string;
  name: string;
  price: number;
  image: string;
  calories: number;
  max: number;
  is_selected?: number;
  is_upsell?: number;
  modifier_group_id?: number;
  modifier_group_min?: number;
  modifier_group_max?: number;
  modifier_group_extendable_limit?: number;
  modifier_location: IModifierLocationRow[];
  core_ingredients_modifier_groups: {
    id: number;
    brink_modifier_group_id: number;
    name: string;
    price: number;
    image: string;
    calories: number;
    min?: number;
    max?: number;
    modifiers_groups_modifiers: {
      id: number;
      brink_modifier_id: number;
      name: string;
      price: number;
      image: string;
      calories: number;
      max: number;
      modifier_location: IModifierLocationRow[];
    }[];
  }[];
}

export interface IItemFlag {
  id: number;
  name: string;
}

export interface ICustomizedItem {
  tags?: any[];
  flags?: IItemFlag[];
  id: number;
  category_id: number;
  name: string;
  sub_title?: string | null;
  image: string;
  description: string | null;
  full_brink_item_id: number | null;
  half_brink_item_id: number | null;
  calories: number | null;
  is_customizable?: boolean;
  full_calories: number | null;
  calories_label?: string | null;
  full_calories_label?: string | null;
  price: number;
  is_active: number;
  protein?: string | null;
  make_time_minutes?: number | null;
  reward_catalog?: number;
  half_brink_item_price: number | null;
  half_brink_item_group_id?: number | null;
  full_brink_item_group_id?: number | null;
  show_in_app?: number;
  is_delivery?: number;
  is_pickup?: number;
  is_third_party?: number;
  created_at?: string;
  half_item_label?: string | null;
  half_item_short_description?: string | null;
  half_item_count?: number | null;
  full_item_label?: string | null;
  full_item_short_description?: string | null;
  full_item_count?: number | null;
  counter_increment?: number | null;
  is_box_lunch?: number;
  is_soup_tray?: number;
  is_sandwich_tray?: number;
  is_salad_tray?: number;
  is_package?: number;
  is_full_size?: number;
  is_single_item?: number;
  is_create_your_own: boolean;
  ezcater_full_item_name?: string | null;
  ezcater_half_item_name?: string | null;
  image_version?: string | null;
  order?: number;
  category_name?: string;
  category_full_loyalty_points?: number | null;
  category_half_loyalty_points?: number | null;
  category_half_loyalty_label?: string | null;
  category_full_loyalty_label?: string | null;
  items_modifier_groups: Items_Modifier_Groups[];
  core_modifiers: ICoreModifiers[];
  items_required_modifier_groups: IItemsRequiredModifierGroups[];
  sub_items_required_modifier_groups?: IItemsRequiredModifierGroups[];
  item_location: IModifierLocationRow[];
  half_loyalty_points?: number | null;
  full_loyalty_points?: number | null;
  complementary_items?: ComplementaryItems[];
  /** Group key → default bucket line (numeric modifier ids in API payload) */
  bucket: IBucket;
  /** Currently applied default modifiers for the item (same shape as group modifiers) */
  is_selected?: IItemSelectedModifier[];
  cart_directly?: boolean;
  is_up_sell_item?: boolean;
  sub_categories?: ICategories[];
  is_tray_item?: boolean;
  base?: number;
  /** Set when hydrating from menu API category row */
  basePrice?: number;
}

/** Alias for menu item payload shape (used by tray / nested menu helpers). */
export type ItemDetails = ICustomizedItem;

export interface IBucket {
  [key: string]: {
    quantity: number;
    modifiers: number[];
    price: number;
  };
}

export interface IModifierGroups {
  id: number;
  name: string;
  label: string;
  brink_modifier_group_id: number;
  min?: number;
  max?: number;
  base?: number;
  extendable_limit?: number;
  modifiers_groups_modifiers: {
    id: number;
    brink_modifier_id: number;
    name: string;
    price: number;
    image: string;
    calories: number;
    max: number;
    is_selected?: number;
    modifier_location: IModifierLocationRow[];
  }[];
}

export interface ICategories {
  id: number;
  name: string;
  image: string;
  items: ICustomizedItem[];
  base?: number;
}

export interface ITry2Combo {
  name: string;
  description: string;
  items: ITry2ComboItem[];
}

export interface IKidsCombo {
  name: string;
  description: string;
  items: IKidsComboItem[];
}

export interface ITry2ComboItem {
  id: number;
  price?: number;
  name: string;
  items_modifier_groups: Items_Modifier_Groups[];
  sub_categories: ICategories[];
  complementary_items?: ComplementaryItems[];
  core_modifiers: ICoreModifiers[];
  items_required_modifier_groups: IItemsRequiredModifierGroups[];
}

export interface IKidsComboItem {
  id: number;
  price: number;
  name: string;
  description: string;
  image: string;
  items_modifier_groups?: Items_Modifier_Groups[];
  complementary_items?: ComplementaryItems[];
  core_modifiers?: ICoreModifiers[];
  items_required_modifier_groups?: IItemsRequiredModifierGroups[];
}

export enum Codes {
  ADD = "ADD",
  SUB = "ADD",
  NO = "NO",
}
export interface IFavoriteItem {
  description: string;
  full_calories: number;
  full_calories_label: string;
  half_calories: number;
  half_calories_label: string;
  id: number;
  image: string;
  item_id: number;
  name: string;
  price: number;
  tags: any[];
  cart_directly: boolean;
  category_id: number;
  half_brink_item_id: number;
  calories: number;
  is_active: boolean;
  is_kids_combo?: boolean;
  is_try_2_combo?: boolean;
}

export interface IItemPathToRoute {
  pathname: string;
  state: {
    isReward?: boolean;
    reward_size?: string;
    routeState?: any;
  };
  search: string;
}

/** Size keys for sub-item / items-as-modifier pricing (half vs full). */
export const mapSizesKey: Record<string, any> = {
  [HALF_SIZE.id]: "size_one_count",
  [FULL_SIZE.id]: "size_two_count",
  basePrice: {
    [HALF_SIZE.id]: "size_one_base",
    [FULL_SIZE.id]: "size_two_base",
  },
};

export interface IItemAsModifiers {
  id: number;
  parentItem: any;
  base: number;
  parentIncrementCount?: number;
  selectedItems: any[];
  byDefaultSelectedItems?: any[];
}

export interface IItemCustomization {
  item: any;
  showCustomizedOptions: boolean;
  showCustomization: boolean;
  handleIngredientSelection: (data: any, id?: number) => void;
  modalShow: boolean;
  handleSyncIngredients: (item: any) => void;
  hideModifierModal: () => void;
  unrepeatedIngredients: any[];
  getOrderButtonState?: (type: string) => boolean;
  autoScrollDivRef: MutableRefObject<HTMLDivElement>;
  firstTimeModal?: boolean;
  CYOIScrollToMissingRequiredArea?: {
    refToScrollToMissingRequiredArea: any[];
    isCYOIRequiredModifierSelected: {
      status: boolean;
      highLightArea: number;
    };
    setIsCYOIRequiredModifierSelected: Dispatch<
      SetStateAction<{ status: boolean; highLightArea: any }>
    >;
  };
  itemAsModifierState?: {
    itemsAsModifier: IItemAsModifiers[];
    setItemsAsModifier: Dispatch<SetStateAction<IItemAsModifiers[]>>;
  };
  openItemInfoModal?: (item: any) => void;
  // firstRef:any
}
